<template>
  <div class="resource-find-manage">
    <el-card class="box-card">
      <div slot="header" class="clearfix">
        <span class="card-title">
          <i class="el-icon-search"></i> 资源代找管理
        </span>
      </div>

      <!-- 搜索筛选 -->
      <el-form :inline="true" :model="searchForm" class="search-form">
        <el-form-item label="状态">
          <el-select v-model="searchForm.status" placeholder="全部状态" clearable style="width: 150px">
            <el-option label="待处理" :value="0"></el-option>
            <el-option label="处理中" :value="1"></el-option>
            <el-option label="已完成" :value="2"></el-option>
            <el-option label="已拒绝" :value="3"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="关键词">
          <el-input 
            v-model="searchForm.keyword" 
            placeholder="资源名称/用户名" 
            style="width: 200px"
            clearable>
          </el-input>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="handleSearch" icon="el-icon-search">搜索</el-button>
          <el-button @click="handleReset" icon="el-icon-refresh-left">重置</el-button>
        </el-form-item>
      </el-form>

      <!-- 统计信息 -->
      <div class="statistics">
        <div class="stat-item">
          <div class="stat-label">待处理</div>
          <div class="stat-value pending">{{ statistics.pending }}</div>
        </div>
        <div class="stat-item">
          <div class="stat-label">处理中</div>
          <div class="stat-value processing">{{ statistics.processing }}</div>
        </div>
        <div class="stat-item">
          <div class="stat-label">已完成</div>
          <div class="stat-value completed">{{ statistics.completed }}</div>
        </div>
        <div class="stat-item">
          <div class="stat-label">已拒绝</div>
          <div class="stat-label rejected">{{ statistics.rejected }}</div>
        </div>
      </div>

      <!-- 数据表格 -->
      <el-table 
        :data="requestList" 
        v-loading="loading"
        border
        stripe
        style="width: 100%; margin-top: 20px">
        <el-table-column prop="id" label="ID" width="80"></el-table-column>
        <el-table-column label="用户信息" width="180">
          <template slot-scope="scope">
            <div>{{ scope.row.nickname || scope.row.username || '用户ID:' + scope.row.frontUserId }}</div>
            <el-tag size="mini" :type="getMembershipTagType(scope.row.membershipLevel)">
              {{ getMembershipLevelName(scope.row.membershipLevel) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="resourceName" label="资源名称" min-width="180"></el-table-column>
        <el-table-column prop="resourceType" label="资源类型" width="100">
          <template slot-scope="scope">
            {{ getResourceTypeName(scope.row.resourceType) }}
          </template>
        </el-table-column>
        <el-table-column label="状态" width="100">
          <template slot-scope="scope">
            <el-tag :type="getStatusTagType(scope.row.status)">
              {{ getStatusText(scope.row.status) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="createTime" label="申请时间" width="160">
          <template slot-scope="scope">
            {{ formatDate(scope.row.createTime) }}
          </template>
        </el-table-column>
        <el-table-column label="操作" width="220" fixed="right">
          <template slot-scope="scope">
            <el-button type="text" size="small" @click="handleView(scope.row)">查看</el-button>
            <el-button type="text" size="small" @click="handleProcess(scope.row)" v-if="scope.row.status === 0">
              处理
            </el-button>
            <el-button type="text" size="small" @click="handleComplete(scope.row)" v-if="scope.row.status === 1">
              完成
            </el-button>
            <el-button type="text" size="small" class="danger" @click="handleDelete(scope.row)">
              删除
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <el-pagination
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
        :current-page="pagination.page"
        :page-sizes="[10, 20, 50, 100]"
        :page-size="pagination.size"
        :total="pagination.total"
        layout="total, sizes, prev, pager, next, jumper"
        style="margin-top: 20px; text-align: right">
      </el-pagination>
    </el-card>

    <!-- 查看详情对话框 -->
    <el-dialog 
      :title="dialogTitle" 
      :visible.sync="detailDialogVisible" 
      width="700px">
      <div v-if="currentRequest" class="detail-content">
        <el-descriptions :column="2" border>
          <el-descriptions-item label="申请人">
            {{ currentRequest.nickname || currentRequest.username || '用户ID:' + currentRequest.frontUserId }}
          </el-descriptions-item>
          <el-descriptions-item label="会员等级">
            <el-tag :type="getMembershipTagType(currentRequest.membershipLevel)">
              {{ getMembershipLevelName(currentRequest.membershipLevel) }}
            </el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="资源名称" :span="2">
            {{ currentRequest.resourceName }}
          </el-descriptions-item>
          <el-descriptions-item label="资源类型">
            {{ getResourceTypeName(currentRequest.resourceType) }}
          </el-descriptions-item>
          <el-descriptions-item label="联系方式">
            {{ currentRequest.contactInfo || '未提供' }}
          </el-descriptions-item>
          <el-descriptions-item label="详细描述" :span="2">
            <div style="white-space: pre-wrap;">{{ currentRequest.resourceDescription }}</div>
          </el-descriptions-item>
          <el-descriptions-item label="申请时间">
            {{ formatDate(currentRequest.createTime) }}
          </el-descriptions-item>
          <el-descriptions-item label="状态">
            <el-tag :type="getStatusTagType(currentRequest.status)">
              {{ getStatusText(currentRequest.status) }}
            </el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="处理管理员" v-if="currentRequest.adminName">
            {{ currentRequest.adminName }}
          </el-descriptions-item>
          <el-descriptions-item label="处理时间" v-if="currentRequest.handleTime">
            {{ formatDate(currentRequest.handleTime) }}
          </el-descriptions-item>
          <el-descriptions-item label="管理员回复" :span="2" v-if="currentRequest.adminReply">
            <div style="white-space: pre-wrap;">{{ currentRequest.adminReply }}</div>
          </el-descriptions-item>
        </el-descriptions>
      </div>
    </el-dialog>

    <!-- 处理对话框 -->
    <el-dialog 
      title="处理资源代找请求" 
      :visible.sync="processDialogVisible" 
      width="600px">
      <el-form :model="processForm" :rules="processRules" ref="processForm" label-width="100px">
        <el-form-item label="处理状态" prop="status">
          <el-radio-group v-model="processForm.status">
            <el-radio :label="1">处理中</el-radio>
            <el-radio :label="2">已完成</el-radio>
            <el-radio :label="3">已拒绝</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="回复内容" prop="adminReply">
          <el-input 
            type="textarea"
            v-model="processForm.adminReply" 
            placeholder="请输入回复内容"
            :rows="5"
            maxlength="500"
            show-word-limit>
          </el-input>
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="processDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="confirmProcess" :loading="processing">确定</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
export default {
  name: 'ResourceFindManage',
  data() {
    return {
      loading: false,
      searchForm: {
        status: null,
        keyword: ''
      },
      statistics: {
        pending: 0,
        processing: 0,
        completed: 0,
        rejected: 0
      },
      requestList: [],
      pagination: {
        page: 1,
        size: 10,
        total: 0
      },
      detailDialogVisible: false,
      processDialogVisible: false,
      currentRequest: null,
      processForm: {
        status: 1,
        adminReply: ''
      },
      processRules: {
        status: [
          { required: true, message: '请选择处理状态', trigger: 'change' }
        ],
        adminReply: [
          { required: true, message: '请输入回复内容', trigger: 'blur' }
        ]
      },
      processing: false
    }
  },
  computed: {
    dialogTitle() {
      return this.currentRequest ? `${this.currentRequest.resourceName} - 详情` : '详情'
    }
  },
  mounted() {
    this.loadList()
    this.loadStatistics()
  },
  methods: {
    async loadList() {
      this.loading = true
      try {
        const params = {
          page: this.pagination.page,
          size: this.pagination.size,
          status: this.searchForm.status,
          keyword: this.searchForm.keyword
        }
        
        console.log('[资源代找] 请求参数:', params)
        const res = await this.$axios.get('/admin/resource-find/list', { params })
        console.log('[资源代找] 响应数据:', res.data)
        
        if (res.data.code === 200) {
          this.requestList = res.data.data.records || []
          this.pagination.total = res.data.data.total || 0
          console.log('[资源代找] 列表数据:', this.requestList)
        } else {
          this.$message.error(res.data.message || '加载失败')
        }
      } catch (error) {
        console.error('[资源代找] 加载列表失败:', error)
        this.$message.error('加载列表失败: ' + (error.response?.data?.message || error.message))
      } finally {
        this.loading = false
      }
    },
    
    async loadStatistics() {
      try {
        const res = await this.$axios.get('/admin/resource-find/statistics')
        if (res.data.code === 200) {
          this.statistics = res.data.data
        }
      } catch (error) {
        console.error('加载统计数据失败:', error)
      }
    },
    
    handleSearch() {
      this.pagination.page = 1
      this.loadList()
    },
    
    handleReset() {
      this.searchForm = {
        status: null,
        keyword: ''
      }
      this.pagination.page = 1
      this.loadList()
    },
    
    handleSizeChange(size) {
      this.pagination.size = size
      this.pagination.page = 1
      this.loadList()
    },
    
    handleCurrentChange(page) {
      this.pagination.page = page
      this.loadList()
    },
    
    handleView(row) {
      this.currentRequest = row
      this.detailDialogVisible = true
    },
    
    handleProcess(row) {
      this.currentRequest = row
      this.processForm = {
        status: 1,
        adminReply: ''
      }
      this.processDialogVisible = true
    },
    
    handleComplete(row) {
      this.currentRequest = row
      this.processForm = {
        status: 2,
        adminReply: ''
      }
      this.processDialogVisible = true
    },
    
    async confirmProcess() {
      this.$refs.processForm.validate(async (valid) => {
        if (!valid) return
        
        this.processing = true
        try {
          const res = await this.$axios.post('/admin/resource-find/update-status', {
            requestId: this.currentRequest.id,
            status: this.processForm.status,
            adminReply: this.processForm.adminReply
          })
          
          if (res.data.code === 200) {
            this.$message.success('处理成功')
            this.processDialogVisible = false
            this.loadList()
            this.loadStatistics()
          } else {
            this.$message.error(res.data.message || '处理失败')
          }
        } catch (error) {
          this.$message.error('处理失败')
        } finally {
          this.processing = false
        }
      })
    },
    
    async handleDelete(row) {
      try {
        await this.$confirm('确定要删除这条请求吗？', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })
        
        const res = await this.$axios.delete(`/admin/resource-find/${row.id}`)
        
        if (res.data.code === 200) {
          this.$message.success('删除成功')
          this.loadList()
          this.loadStatistics()
        } else {
          this.$message.error(res.data.message || '删除失败')
        }
      } catch (error) {
        if (error !== 'cancel') {
          this.$message.error('删除失败')
        }
      }
    },
    
    getStatusText(status) {
      const map = {
        0: '待处理',
        1: '处理中',
        2: '已完成',
        3: '已拒绝'
      }
      return map[status] || '未知'
    },
    
    getStatusTagType(status) {
      const map = {
        0: 'warning',
        1: 'primary',
        2: 'success',
        3: 'danger'
      }
      return map[status] || 'info'
    },
    
    getMembershipLevelName(level) {
      const map = {
        0: '普通会员',
        1: '高级会员',
        2: '专业会员'
      }
      return map[level] || '未知'
    },
    
    getMembershipTagType(level) {
      const map = {
        0: 'info',
        1: 'warning',
        2: 'danger'
      }
      return map[level] || 'info'
    },
    
    getResourceTypeName(type) {
      const map = {
        'software': '软件工具',
        'document': '技术文档',
        'video': '视频教程',
        'sourcecode': '源码项目',
        'other': '其他'
      }
      return map[type] || type
    },
    
    formatDate(date) {
      if (!date) return '-'
      return new Date(date).toLocaleString('zh-CN')
    }
  }
}
</script>

<style scoped>
.resource-find-manage {
  padding: 20px;
}

.card-title {
  font-size: 18px;
  font-weight: bold;
}

.search-form {
  margin-bottom: 20px;
}

.statistics {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  margin-bottom: 20px;
}

.stat-item {
  background: #f5f7fa;
  padding: 20px;
  border-radius: 8px;
  text-align: center;
}

.stat-label {
  font-size: 14px;
  color: #909399;
  margin-bottom: 8px;
}

.stat-value {
  font-size: 28px;
  font-weight: bold;
}

.stat-value.pending {
  color: #E6A23C;
}

.stat-value.processing {
  color: #409EFF;
}

.stat-value.completed {
  color: #67C23A;
}

.stat-value.rejected {
  color: #F56C6C;
}

.danger {
  color: #F56C6C;
}

.detail-content {
  padding: 10px 0;
}
</style>
